import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  addGroupToRole,
  getGroupsForRole,
  getRoleForApplication,
  setBreadcrumbs,
} from "../../../actions";
import { Card, Space, Typography } from "antd";
import GroupRow from "./GroupRow";
import GroupSearch from "../../groups/GroupSearch";
import LoAString from "../../loa/LoAString";
import { getActiveRole, getGroups, isLoading } from "../../../selectors";
import {
  FullPageLoader,
  ContentTable,
} from "@authzsvc/common-react-components";
import { boolToCheckmark } from "./roleUtils";
import { errorAction, types as at } from "@authzsvc/api-lib";
import { showErrorNotification } from "../../../utils";
import { sidebar_groups } from "../../../config";

const { Title } = Typography;

class EditRole extends React.Component {
  static propTypes = {
    loading: PropTypes.bool,
    groups: PropTypes.arrayOf(PropTypes.object),
    role: PropTypes.object,
    actions: PropTypes.shape({
      getRoleForApplication: PropTypes.func,
      setBreadcrumbs: PropTypes.func,
      getGroupsForRole: PropTypes.func,
      addGroupToRole: PropTypes.func,
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
    breadcrumbs: PropTypes.array,
    applicationid: PropTypes.string.isRequired,
    roleid: PropTypes.string.isRequired,
  };

  componentDidMount() {
    const { applicationid, roleid, actions, breadcrumbs } = this.props;

    actions.getRoleForApplication(applicationid, roleid);
    actions.getGroupsForRole(applicationid, roleid);
    actions.setBreadcrumbs(breadcrumbs);
  }

  handleAdd = async (result) => {
    const { pathname } = this.props.location;
    const applicationid = pathname.split("/")[2];
    const roleid = pathname.split("/")[4];

    const resp = await this.props.actions.addGroupToRole(
      applicationid,
      roleid,
      result.group.id,
    );

    if (resp.type === errorAction(at.ADD_GROUP_TO_ROLE)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }
  };

  getColumns = () => [
    {
      title: "Identifier",
      dataIndex: "groupIdentifier",
      key: "groupIdentifier",
      searchable: true,
      render: (text, group) => (
        <a
          id="to-group-portal"
          target="_blank"
          rel="noopener noreferrer"
          href={`${sidebar_groups}/group/${group.groupIdentifier}/details`}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Display Name",
      dataIndex: "displayName",
      key: "displayName",
      searchable: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "",
      key: "actions",
      render: (_, group) => (
        <GroupRow
          group={group}
          roleid={this.props.role.id}
          applicationid={this.props.role.applicationId}
        />
      ),
    },
  ];

  render() {
    const { role, loading } = this.props;
    const linkedGroups = Array.from(
      new Map((role.groups || []).map((g) => [g.id, g])).values(),
    );

    if (loading) {
      return <FullPageLoader />;
    }

    return (
      <div style={{ padding: 24 }}>
        <Title level={3}>Role: {role.displayName}</Title>

        <ContentTable
          loading={false}
          pagination={false}
          columns={[
            { title: "Name", dataIndex: "name", key: "name" },
            {
              title: "Role Identifier",
              dataIndex: "identifier",
              key: "identifier",
            },
            { title: "Minimum Assurance", dataIndex: "loa", key: "loa" },
            {
              title: "Multifactor",
              dataIndex: "multifactor",
              key: "multifactor",
            },
            { title: "Required", dataIndex: "required", key: "required" },
            {
              title: "Apply to all users",
              dataIndex: "applyToAllUsers",
              key: "applyToAllUsers",
            },
          ]}
          dataSource={[
            {
              key: "role-data",
              name: role.displayName,
              identifier: role.name,
              loa: <LoAString id={role.minimumLoaId} />,
              multifactor: boolToCheckmark(role.multifactor),
              required: boolToCheckmark(role.required),
              applyToAllUsers: boolToCheckmark(role.applyToAllUsers),
            },
          ]}
        />

        <Title level={4}>Linked Groups</Title>

        <ContentTable
          loading={loading}
          columns={this.getColumns()}
          dataSource={linkedGroups}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: "You do not have any linked groups yet!" }}
          style={{ marginBottom: 16 }}
        />

        <Card type="inner" title={`Search by group identifier`}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <GroupSearch
              className="wider-search"
              isEditingMembership={false}
              handleResultSelect={this.handleAdd}
            />
          </Space>
        </Card>
      </div>
    );
  }
}

export default connect(
  (state) => ({
    groups: getGroups(state),
    role: getActiveRole(state),
    loading: isLoading(state, "getRoleForApplication"),
  }),
  (dispatch) => ({
    actions: bindActionCreators(
      {
        getRoleForApplication,
        setBreadcrumbs,
        getGroupsForRole,
        addGroupToRole,
      },
      dispatch,
    ),
  }),
)(EditRole);
